// Utilities for Strategy Schema V2 context: hashing and normalization

// Heuristic category mapping from name
function inferCategory(name = '') {
  const n = String(name).toUpperCase();
  if (/(PIVOT|SWING)/.test(n)) return 'Structure';
  if (/(CANDLE|PATTERN)/.test(n)) return 'Pattern';
  if (/(ATR|VOL|VOLA)/.test(n)) return 'Volatility';
  if (/(EMA|SMA|MA|TREND)/.test(n)) return 'Trend';
  if (/(RSI|MACD|OSC|MOMENT)/.test(n)) return 'Momentum';
  if (/(LOGIC|BOOL)/.test(n)) return 'Logic';
  return 'Price';
}

/**
 * Lightweight hashing util
 * - Prefers WebCrypto SHA-256 in secure contexts
 * - Falls back to FNV-1a (non-crypto) hash if SubtleCrypto is unavailable
 */
export async function hashJson(obj) {
  const json = JSON.stringify(obj);
  const data = new TextEncoder().encode(json);
  const subtle = (typeof crypto !== 'undefined' && crypto && crypto.subtle) ? crypto.subtle : undefined;
  if (subtle && typeof subtle.digest === 'function') {
    const digest = await subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback: FNV-1a 32-bit, returned as 8-hex prefixed with 'fnv-'
  let h = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    h ^= data[i];
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return 'fnv-' + ('0000000' + h.toString(16)).slice(-8);
}

export function normalize(raw) {
  if (!raw) return { indicators: [], operators: [], actions: [] };

  const out = { indicators: [], operators: [], actions: [] };

  const indSchema = raw.properties?.indicators;
  const opSchema = raw.properties?.operators;
  const actSchema = raw.properties?.actions;

  const extractList = (schemaNode, kind) => {
    if (!schemaNode) return [];
    const items = schemaNode.items || schemaNode.anyOf || schemaNode.oneOf || schemaNode.allOf || schemaNode;
    let candidates = [];
    if (Array.isArray(items.anyOf)) candidates = items.anyOf;
    else if (Array.isArray(items.oneOf)) candidates = items.oneOf;
    else if (Array.isArray(items.allOf)) candidates = items.allOf;
    else if (Array.isArray(schemaNode.anyOf)) candidates = schemaNode.anyOf;
    else if (Array.isArray(schemaNode.oneOf)) candidates = schemaNode.oneOf;
    else if (Array.isArray(schemaNode.allOf)) candidates = schemaNode.allOf;

    // items.properties.name.enum + items.allOf (per-name branches)
    const itemDef = schemaNode.items && typeof schemaNode.items === 'object' ? schemaNode.items : undefined;
    const nameEnums = itemDef?.properties?.name?.enum;
    const perNameBranches = Array.isArray(itemDef?.allOf) ? itemDef.allOf : [];
    if (Array.isArray(nameEnums) && nameEnums.length) {
      const mapByName = new Map();
      for (const branch of perNameBranches) {
        const constName = branch?.if?.properties?.name?.const;
        if (constName) mapByName.set(String(constName), branch);
      }
      return nameEnums.map((n) => {
        const name = String(n);
        const branch = mapByName.get(name) || mapByName.get(name.toLowerCase()) || mapByName.get(name.toUpperCase());
        const paramsProps = branch?.then?.properties?.params?.properties
          || itemDef?.properties?.params?.properties
          || {};
        const params = Object.entries(paramsProps).map(([pName, pSchema]) => ({
          name: pName,
          type: pSchema.type || 'string',
          enum: pSchema.enum,
          min: pSchema.minimum,
          max: pSchema.maximum,
          default: pSchema.default,
          required: (branch?.then?.properties?.params?.required || []).includes(pName),
          description: pSchema.description,
          x_order: pSchema.x_order ?? pSchema['x-order'],
        })).sort((a,b) => ((a.x_order ?? Infinity) - (b.x_order ?? Infinity)));
        const outputs = branch?.then?.['x-outputs'] || [];
        return {
          kind,
          name,
          category: (branch?.then && branch.then['x-category']) || inferCategory(name),
          params,
          outputs,
          paneType: branch?.then?.['x-pane_type'],
          visual: branch?.then?.['x-visual_defaults'] ? { defaults: branch.then['x-visual_defaults'], schema: branch.then['x-visual_schema'] } : undefined,
        };
      });
    }

    // Fallback: treat discovered candidates as node schemas
    return (candidates || [])
      .map(c => {
        const name = c?.properties?.name?.const || c?.properties?.name?.default || c?.const || c?.title || c?.$id;
        if (!name) return null;
        const paramsProps = c?.properties?.params?.properties || {};
        const params = Object.entries(paramsProps).map(([pName, pSchema]) => ({
          name: pName,
          type: pSchema.type || 'string',
          enum: pSchema.enum,
          min: pSchema.minimum,
          max: pSchema.maximum,
          default: pSchema.default,
          required: (c?.properties?.params?.required || []).includes(pName),
          description: pSchema.description,
          x_order: pSchema.x_order ?? pSchema['x-order'],
        })).sort((a,b) => ((a.x_order ?? Infinity) - (b.x_order ?? Infinity)));
        const outputs = c['x-outputs'] || c?.properties?.outputs?.enum || [];
        return {
          kind,
          name,
          category: c['x-category'] || inferCategory(name),
          params,
          outputs,
          paneType: c['x-pane_type'],
          visual: c['x-visual_defaults'] ? { defaults: c['x-visual_defaults'], schema: c['x-visual_schema'] } : undefined,
        };
      })
      .filter(Boolean);
  };

  const reportIssues = [];
  const validate = (list, k) => list.filter(d => {
    if (!d.name) { reportIssues.push(`[schema] ${k} missing name`); return false; }
    return true;
  });

  out.indicators = validate(extractList(indSchema, 'indicator'), 'indicator');
  out.operators = validate(extractList(opSchema, 'operator'), 'operator');
  out.actions = validate(extractList(actSchema, 'action'), 'action');

  if (out.indicators.length === 0 && out.operators.length === 0 && out.actions.length === 0) {
    const xIndicators = raw['x-indicators'] || raw['x_nodes']?.indicators || raw['x-nodes']?.indicators;
    const xOperators = raw['x-operators'] || raw['x_nodes']?.operators || raw['x-nodes']?.operators;
    const xActions = raw['x-actions'] || raw['x_nodes']?.actions || raw['x-nodes']?.actions;
    const fromX = (arr, k) => Array.isArray(arr) ? arr.map(c => ({
      kind: k,
      name: c.name || c.title || c.$id,
      category: c.category || inferCategory(c.name || c.title || c.$id || ''),
      params: c.params || [],
      outputs: c.outputs || c['x-outputs'] || [],
    })).filter(x => x && x.name) : [];
    out.indicators = fromX(xIndicators, 'indicator');
    out.operators = fromX(xOperators, 'operator');
    out.actions = fromX(xActions, 'action');
  }

  if (reportIssues.length) {
    console.warn('Strategy schema issues:', reportIssues);
  }

  return out;
}
